import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";

type DiffSummary = components["schemas"]["InventoryDiffSummary"];

interface DiffKpiStripProps {
  summary: DiffSummary;
}

interface KpiCardProps {
  label: string;
  value: string;
  testId: string;
}

function KpiCard({ label, value, testId }: KpiCardProps): JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-[var(--sp-1)] rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-[var(--sp-4)] py-[var(--sp-3)] shadow-[var(--sh-1)]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
        {label}
      </span>
      <span data-testid={testId} className="num text-[20px] font-bold text-[var(--text-primary)]">
        {value}
      </span>
    </div>
  );
}

/**
 * Four KPI tiles above the table. `Threshold` shows BOTH halves of the D4 rule
 * ("5 % / 2 pcs") — showing only the percentage is what made a 1-unit gap on a
 * 2-unit SKU look like a 50% disaster.
 */
export function DiffKpiStrip({ summary }: DiffKpiStripProps): JSX.Element {
  const { t, i18n } = useTranslation("inventory_diff");
  const nf = new Intl.NumberFormat(i18n.language);

  return (
    <div className="mb-[var(--sp-4)] flex gap-[var(--sp-3)]">
      <KpiCard
        label={t("kpi.skus_checked")}
        value={nf.format(summary.skus_checked)}
        testId="diff-kpi-skus"
      />
      <KpiCard
        label={t("kpi.over_threshold")}
        value={nf.format(summary.over_threshold_count)}
        testId="diff-kpi-over"
      />
      <KpiCard
        label={t("kpi.threshold")}
        value={t("kpi.threshold_value", {
          pct: Math.round(summary.threshold_pct * 100),
          pcs: summary.threshold_min_pcs,
        })}
        testId="diff-kpi-threshold"
      />
      <KpiCard
        label={t("kpi.alert")}
        value={summary.alert_triggered ? t("alert.sent") : t("alert.none")}
        testId="diff-kpi-alert"
      />
    </div>
  );
}
